import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import EventCard from "@/components/EventCard";
import Reveal from "@/components/Reveal";
import EmailCaptureCTA from "@/components/EmailCaptureCTA";

const today = () => new Date().toISOString().slice(0, 10);

function EmptyState({ user }) {
  return (
    <Reveal className="max-w-2xl mx-auto text-center py-10" >
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-gold/40 bg-gold/5 mb-8">
        <Sparkles size={26} className="text-gold" strokeWidth={1.25} />
      </div>
      <p className="font-script text-3xl text-fuchsia mb-3">Something beautiful is coming.</p>
      <h2 className="font-serif text-4xl sm:text-5xl font-light text-cream mb-6 leading-tight">The first gatherings are being curated</h2>
      <p className="text-cream/70 text-lg leading-relaxed font-light mb-10">
        The Mahogany Room is more than a book club — it's a space for connection, conversation,
        and unforgettable gatherings. Our first experiences are currently being planned.
        {user ? " You're in The Room — we'll let you know the moment they're announced." : " Join The Room to be among the first to know when new meetups and events are announced."}
      </p>
      {user ? (
        <Link to="/dashboard" className="btn-primary" data-testid="events-empty-cta-member">Go to My Dashboard</Link>
      ) : (
        <Link to="/join" className="btn-primary" data-testid="events-empty-cta-join">Join The Room</Link>
      )}
    </Reveal>
  );
}

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data || [])).finally(() => setLoaded(true));
  }, [user]);

  const t = today();
  const upcoming = events.filter((e) => (e.date || "") >= t).sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
  const past = events.filter((e) => (e.date || "") < t).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const hasAny = upcoming.length > 0 || past.length > 0;

  return (
    <div data-testid="events-page">
      <PageHeader overline="Gather With Us" script="Beautiful gatherings." title="Meetups & Events" subtitle="Brunches, dinners, wine nights, reading nights, and cultural experiences — thoughtfully curated for connection." />

      <section className="pb-24 bg-mahogany-900 pt-16">
        <div className="max-w-7xl mx-auto px-6">
          {!loaded ? (
            <p className="text-cream/40 text-center py-16">Loading gatherings…</p>
          ) : !hasAny ? (
            <EmptyState user={user} />
          ) : (
            <>
              {upcoming.length > 0 && (
                <div className="mb-20" data-testid="upcoming-events">
                  <p className="overline mb-8 text-center">Upcoming Gatherings</p>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {upcoming.map((e, i) => <Reveal key={e.id} delay={i * 0.05}><EventCard event={e} /></Reveal>)}
                  </div>
                </div>
              )}
              {past.length > 0 && (
                <div data-testid="past-events">
                  <p className="overline mb-2 text-center">Our Gatherings</p>
                  <h3 className="font-serif text-3xl text-cream text-center mb-8 font-light">Moments we've shared</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {past.map((e, i) => <Reveal key={e.id} delay={i * 0.05}><EventCard event={e} past /></Reveal>)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <EmailCaptureCTA />
    </div>
  );
}
